
/**
 * Campaign related functions for Meta API
 */
import { MetaCampaign, CampaignInsightsFields } from './types';
import { API_VERSION } from './constants';

/**
 * Get campaigns for a specific Meta ad account with detailed insights
 */
export async function getCampaigns(accountId: string, accessToken: string): Promise<MetaCampaign[]> {
  try {
    console.log('Getting campaigns for account directly:', accountId);
    
    // Request more detailed campaign information and insights
    // Removed relevance_score which is no longer supported in Meta API
    const fields = [
      'name',
      'status',
      'objective',
      'spend',
      'adsets.limit(1).summary(true)',
      'start_time',
      'stop_time',
      'insights{clicks,impressions,reach,cpc,ctr,cpm,actions,conversions,cost_per_action_type,engagement_rate_ranking,quality_ranking,conversion_rate_ranking,frequency,unique_clicks,website_ctr,cost_per_unique_click,cost_per_inline_link_click,video_p75_watched_actions,canvas_avg_view_time,estimated_ad_recallers,estimated_ad_recall_rate,inline_link_clicks,inline_post_engagement,unique_inline_link_click_ctr}',
      'targeting',
      'bid_strategy',
      'budget_remaining',
      'daily_budget',
      'lifetime_budget'
    ].join(',');
    
    const campaignsUrl = `https://graph.facebook.com/${API_VERSION}/${accountId}/campaigns?fields=${fields}&time_range={"since":"2023-01-01","until":"${new Date().toISOString().split('T')[0]}"}&access_token=${accessToken}`;
    
    console.log('Fetching campaigns from URL (without sensitive info):', 
      campaignsUrl.replace(accessToken, 'ACCESS_TOKEN_HIDDEN'));
      
    const campaignsResponse = await fetch(campaignsUrl);
    
    if (!campaignsResponse.ok) {
      const errorText = await campaignsResponse.text();
      console.error('Campaigns fetch failed:', errorText);
      
      try {
        const errorData = JSON.parse(errorText);
        if (errorData.error) {
          throw new Error(`Meta API Error (${errorData.error.code}): ${errorData.error.message}`);
        }
      } catch (parseError) {
        throw new Error(`Campaigns fetch failed: ${errorText}`);
      }
    }
    
    const campaignsData = await campaignsResponse.json();
    
    if (!campaignsData.data) {
      throw new Error('No campaigns data found in response');
    }
    
    console.log('Got campaigns data, processing...');
    
    return campaignsData.data.map((campaign: any) => {
      const insights = campaign.insights?.data?.[0] || {};
      
      // Extract all types of actions
      const extractActionValue = (actionType: string) => {
        const actions = insights.actions?.find((action: any) => action.action_type === actionType);
        return actions ? parseInt(actions.value) : 0;
      };
      
      const leads = extractActionValue('lead');
      const conversions = extractActionValue('offsite_conversion');
      const engagement = extractActionValue('post_engagement');
      const linkClicks = extractActionValue('link_click');
      const videoViews = extractActionValue('video_view');
      
      // Ensure spend/budget is properly parsed as a number
      const spend = campaign.spend ? parseFloat(campaign.spend) : 0;
      
      return {
        id: campaign.id,
        name: campaign.name,
        status: campaign.status,
        objective: campaign.objective || 'UNKNOWN',
        budget: spend,
        budgetRemaining: parseFloat(campaign.budget_remaining || 0),
        dailyBudget: parseFloat(campaign.daily_budget || 0),
        lifetimeBudget: parseFloat(campaign.lifetime_budget || 0),
        clicks: parseInt(insights.clicks || 0),
        impressions: parseInt(insights.impressions || 0),
        reach: parseInt(insights.reach || 0),
        frequency: parseFloat(insights.frequency || 0),
        ctr: parseFloat(insights.ctr || 0),
        cpc: parseFloat(insights.cpc || 0),
        cpm: parseFloat(insights.cpm || 0),
        leads,
        conversions,
        engagement,
        linkClicks,
        videoViews,
        qualityRanking: insights.quality_ranking || 'UNKNOWN',
        engagementRateRanking: insights.engagement_rate_ranking || 'UNKNOWN',
        conversionRateRanking: insights.conversion_rate_ranking || 'UNKNOWN',
        startDate: campaign.start_time ? campaign.start_time.split('T')[0] : null,
        endDate: campaign.stop_time ? campaign.stop_time.split('T')[0] : null,
        adSets: campaign.adsets?.summary?.total_count || 0,
        targeting: campaign.targeting || {},
        bidStrategy: campaign.bid_strategy,
        uniqueClicks: parseInt(insights.unique_clicks || 0),
        costPerUniqueClick: parseFloat(insights.cost_per_unique_click || 0),
        websiteCtr: parseFloat(insights.website_ctr?.[0]?.value || 0),
        estimatedAdRecallRate: parseFloat(insights.estimated_ad_recall_rate || 0)
      };
    });
  } catch (error) {
    console.error('Error getting campaigns:', error);
    throw error;
  }
}

/**
 * Get campaign insights with specific date range and metrics
 */
export async function getCampaignInsights(
  campaignId: string, 
  accessToken: string, 
  datePreset: string = 'last_30_days',
  dateRange?: {
    startDate: string;
    endDate: string;
  }
): Promise<CampaignInsightsFields> {
  try {
    console.log(`Getting insights for campaign: ${campaignId}`);
    
    // Updated fields list - removed relevance_score which is deprecated
    const fields = [
      'impressions', 
      'clicks', 
      'spend', 
      'reach', 
      'cpc', 
      'cpm', 
      'ctr', 
      'actions', 
      'conversions',
      'cost_per_action_type',
      'unique_clicks',
      'frequency',
      'quality_ranking',
      'engagement_rate_ranking',
      'conversion_rate_ranking'
    ].join(',');
    
    // Build URL based on whether we're using a preset or custom date range
    let insightsUrl;
    
    if (datePreset === 'custom' && dateRange) {
      const timeRange = JSON.stringify({
        since: dateRange.startDate,
        until: dateRange.endDate
      });
      
      insightsUrl = `https://graph.facebook.com/${API_VERSION}/${campaignId}/insights?fields=${fields}&time_range=${encodeURIComponent(timeRange)}&access_token=${accessToken}`;
      console.log(`Using custom date range: ${dateRange.startDate} to ${dateRange.endDate}`);
    } else {
      insightsUrl = `https://graph.facebook.com/${API_VERSION}/${campaignId}/insights?fields=${fields}&date_preset=${datePreset}&access_token=${accessToken}`;
      console.log(`Using date preset: ${datePreset}`);
    }
    
    console.log('Fetching insights from URL (without sensitive info):', 
      insightsUrl.replace(accessToken, 'ACCESS_TOKEN_HIDDEN'));
    
    const response = await fetch(insightsUrl);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Campaign insights fetch failed:', errorText);
      throw new Error(`Failed to fetch campaign insights: ${errorText}`);
    }
    
    const data = await response.json();
    console.log(`Got insights data for campaign ${campaignId}, processing...`);
    
    return data.data?.[0] || {};
  } catch (error) {
    console.error('Error getting campaign insights:', error);
    throw error;
  }
}
